import useContentPermissions from "@src/features/libraryItem/hooks/useContentPermissions";
import { ContentPermissionsFragment } from "@src/features/libraryItem/hooks/useContentPermissions.generated";
import { useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import { isEqual } from "lodash";
import { useMemo } from "react";
import { publishedVersionSelectedAtom } from "@src/features/libraryItem/module/atoms";
import {
  useActiveLibraryItemMainTab,
  useIsRedesignedLibraryItemPage,
} from "@src/features/libraryItem/contexts/libraryItemMainTabStore";
import { courseVersionCourseIdAtom } from "../atoms";

const useCourseBuilderContentPermissions = ():
  | ContentPermissionsFragment
  | undefined => {
  const input = useAtomValue(
    useMemo(
      () =>
        selectAtom(
          courseVersionCourseIdAtom,
          (courseId) => ({ courseId }),
          isEqual,
        ),
      [],
    ),
  );
  const permissions = useContentPermissions(input);
  // On the redesigned module page a course opened inside the module follows
  // the module's canvas: read-only while the module's published version is
  // selected, and on the module's "preview" tab, which is the read-only view
  // of the draft. Neither applies on the course's own page or the previous
  // module page — the store reports nothing there — so those keep their own
  // rules.
  const redesigned = useIsRedesignedLibraryItemPage();
  const publishedSelected = !!useAtomValue(publishedVersionSelectedAtom);
  const modulePublishedSelected = redesigned && publishedSelected;
  const viewingTab = useActiveLibraryItemMainTab() === "preview";

  return useMemo(() => {
    if (!permissions) return undefined;
    if (!modulePublishedSelected && !viewingTab) return permissions;
    return { ...permissions, hasEditContentPermission: false };
  }, [permissions, modulePublishedSelected, viewingTab]);
};

export default useCourseBuilderContentPermissions;
